"use client";

import { useState, useMemo } from "react";
import { BookFilmCard } from "./book-film-card";
import { formatMediaType } from "@/lib/books-films";
import type { BookFilm, RowingRole, MediaType } from "@/types";

interface BooksFilmsClientProps {
  items: BookFilm[];
  mediaTypes: MediaType[];
}

export function BooksFilmsClient({ items, mediaTypes }: BooksFilmsClientProps) {
  const [activeRole, setActiveRole] = useState<RowingRole>("about");
  const [selectedMediaType, setSelectedMediaType] = useState<MediaType | null>(
    null
  );

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (item.rowingRole !== activeRole) return false;
      if (selectedMediaType !== null && item.mediaType !== selectedMediaType)
        return false;
      return true;
    });
  }, [items, activeRole, selectedMediaType]);

  return (
    <div className="space-y-6">
      {/* Role tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            setActiveRole("about");
            setSelectedMediaType(null);
          }}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeRole === "about"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          About Rowing
        </button>
        <button
          onClick={() => {
            setActiveRole("featuring");
            setSelectedMediaType(null);
          }}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeRole === "featuring"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          Featuring Rowing
        </button>
        <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
          {filteredItems.length}{" "}
          {filteredItems.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {/* Media type filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedMediaType(null)}
          className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
            selectedMediaType === null
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
          }`}
        >
          All
        </button>
        {mediaTypes.map((type) => (
          <button
            key={type}
            onClick={() =>
              setSelectedMediaType(selectedMediaType === type ? null : type)
            }
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
              selectedMediaType === type
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {formatMediaType(type)}
          </button>
        ))}
      </div>

      {/* Grid */}
      {filteredItems.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map((item) => (
            <BookFilmCard key={item.slug} item={item} />
          ))}
        </div>
      ) : (
        <p className="py-12 text-center text-gray-500 dark:text-gray-400">
          No entries found for this filter combination.
        </p>
      )}
    </div>
  );
}
